import React, { memo, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { $getNodeByKey, $getRoot, LexicalEditor as LexicalEditorType } from 'lexical';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { ContactInteractionInputManagerContext } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-manager';
import {
  LexicalEditor,
  LexicalEditorProps,
} from 'src/components/pages/conversations/components/general/lexical-editor/lexical-editor';
import { TemplateCustomPlaceholderResolver } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/placeholders-resolvers/template-custom-placeholder-resolver';
import { TemplatePresetPlaceholderResolver } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/placeholders-resolvers/template-preset-placeholder-resolver';
import { RESET_EDITOR_COMMAND } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/custom-clear-editor-plugin';
import { channelLabelKeyMapBySlug } from '../shared';

const validatePlaceholderNodes = (editor: LexicalEditorType) => {
  let hasInvalidParameters = false;

  editor.update(() => {
    const presetNodes: TemplatePresetPlaceholderResolver[] = [];
    const customNodes: TemplateCustomPlaceholderResolver[] = [];

    const removePresetNodesMutationListener = editor.registerMutationListener(
      TemplatePresetPlaceholderResolver,
      (nodeMap) => {
        nodeMap.entries().forEach(([nodeKey]) => {
          const node = $getNodeByKey(nodeKey) as TemplatePresetPlaceholderResolver;

          presetNodes.push(node);
        });
      },
      {
        skipInitialization: false,
      },
    );

    const removeCustomNodesMutationListener = editor.registerMutationListener(
      TemplateCustomPlaceholderResolver,
      (nodeMap) => {
        nodeMap.entries().forEach(([nodeKey]) => {
          const node = $getNodeByKey(nodeKey) as TemplateCustomPlaceholderResolver;

          customNodes.push(node);
        });
      },
      {
        skipInitialization: false,
      },
    );

    [...presetNodes, ...customNodes].forEach((node) => {
      if (!node.isValid()) {
        node.getWritable().hasError = true;
        hasInvalidParameters = true;
      }
    });

    removePresetNodesMutationListener();
    removeCustomNodesMutationListener();
  });

  return hasInvalidParameters;
};

type CustomEditorProps = Pick<
  LexicalEditorProps,
  | 'pluginConfig'
  | 'isInputEnabled'
  | 'rightRailLeadingContent'
  | 'topContent'
  | 'bottomContent'
> & {
  handleSubmission: (editor: LexicalEditorType) => Promise<void>;
  isSubmitting: boolean;
  placeholderOverride?: string;
  forceFocus?: boolean;
  forceEnableSendButton?: boolean;
  isLoading?: boolean;
  showInput?: boolean;
};

export const CustomEditor = memo<CustomEditorProps>(
  ({
    handleSubmission,
    isSubmitting,
    bottomContent,
    topContent,
    placeholderOverride,
    rightRailLeadingContent,
    pluginConfig,
    isInputEnabled,
    forceFocus = false,
    forceEnableSendButton = false,
    isLoading = false,
    showInput,
  }) => {
    const showNotification = useShowNotification();
    const { t } = useTranslation(['conversations']);
    const {
      messageValues,
      setMessageValues,
      attachmentList,
      setAttachmentList,
      hasFocus,
      setHasFocus,
      clearMessageInputStates,
      selectedInteraction,
      conversationId,
      setInputAreaMode,
      inputAreaMode,
      selectedChannelSlug,
      selectedChannelMessageConstraints,
    } = useContext(ContactInteractionInputManagerContext);

    useEffect(() => {
      if (forceFocus) {
        setHasFocus(true);
      }
    }, [forceFocus, setHasFocus]);

    const hasValidAttachments = useMemo(
      () =>
        attachmentList.length > 0 &&
        attachmentList.some((attachment) =>
          ['UPLOADING', 'UPLOADED'].includes(attachment.status),
        ),
      [attachmentList],
    );

    const isUploadingFiles = useMemo(
      () =>
        attachmentList.some(
          (attachment) => attachment.status !== 'ERROR' && attachment.url === null,
        ),
      [attachmentList],
    );

    const canSendMessage = useMemo(
      () =>
        !!conversationId &&
        (!!messageValues.message || hasValidAttachments || isUploadingFiles),
      [conversationId, hasValidAttachments, isUploadingFiles, messageValues.message],
    );

    return (
      <LexicalEditor
        selectedChannel={selectedChannelSlug}
        setHasFocus={setHasFocus}
        setInputAreaMode={setInputAreaMode}
        selectedInteraction={selectedInteraction}
        pluginConfig={pluginConfig}
        isInputEnabled={isInputEnabled}
        isLoading={isLoading}
        enableSendButton={(forceEnableSendButton || canSendMessage) && !isSubmitting}
        handleSendMessage={async (editor) => {
          if (isUploadingFiles) {
            showNotification({
              title: t(
                'conversations:messageInput.options.attachments.errorMessages.fileStillUploading',
              ),
              type: 'warning',
            });

            return;
          }

          const hasInvalidParameters = validatePlaceholderNodes(editor);

          if (hasInvalidParameters) {
            showNotification({
              title: t(
                'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.messages.missingParameters.title',
              ),
              message: t(
                'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.messages.missingParameters.message',
              ),
              type: 'warning',
            });

            return;
          }

          if (
            selectedChannelSlug &&
            selectedChannelMessageConstraints?.maxAllowedCharacters &&
            messageValues.message.length >
              selectedChannelMessageConstraints.maxAllowedCharacters
          ) {
            const channelLabelKey = channelLabelKeyMapBySlug[selectedChannelSlug];

            showNotification({
              title: t(
                'conversations:messageInput.modes.reply.errorMessages.characterLimitExceeded.title',
              ),
              message: t(
                'conversations:messageInput.modes.reply.errorMessages.characterLimitExceeded.body',
                {
                  channel: t(channelLabelKey),
                  limit:
                    selectedChannelMessageConstraints.maxAllowedCharacters.toLocaleString(),
                },
              ),
              type: 'warning',
            });

            return;
          }

          await handleSubmission(editor);
        }}
        hasFocus={hasFocus || hasValidAttachments}
        isFullHeight={false}
        bottomContent={bottomContent}
        topContent={topContent}
        onChange={(editorState) => {
          editorState.read(() => {
            const root = $getRoot();

            setMessageValues({
              message: root.getTextContent(),
              rawMessage: JSON.stringify(editorState.toJSON()),
            });
          });
        }}
        inputAreaMode={inputAreaMode}
        resetEditorStateHandler={(editor) => {
          editor.dispatchCommand(RESET_EDITOR_COMMAND, clearMessageInputStates);
        }}
        placeholder={
          placeholderOverride ??
          t('conversations:messageInput.modes.reply.inputPlaceholder')
        }
        onFocus={() => setHasFocus(true)}
        attachmentList={attachmentList}
        setAttachmentList={setAttachmentList}
        rightRailLeadingContent={hasFocus ? rightRailLeadingContent : null}
        showInput={showInput}
      />
    );
  },
);
